import { StateCreator } from "zustand";

// --- State Type ---
export type OnboardingSliceState = {
  /** Map of programId -> current step index */
  currentStepByProgram: Record<number, number>;
  /** Map of programId -> array of skipped step indices */
  skippedStepsByProgram: Record<number, number[]>;
};

// --- Actions Type ---
export type OnboardingSliceActions = {
  /** Set the current step for a program */
  setCurrentStep: (programId: number, step: number) => void;
  /** Mark a step as skipped for a program */
  skipStep: (programId: number, stepIndex: number) => void;
  /** Reset onboarding progress for a program */
  resetOnboarding: (programId: number) => void;
  /** Clear all onboarding progress */
  clearAllOnboarding: () => void;
};

// --- Combined Type ---
export type OnboardingSlice = OnboardingSliceState & OnboardingSliceActions;

// --- Initial State ---
export const initialOnboardingState: OnboardingSliceState = {
  currentStepByProgram: {},
  skippedStepsByProgram: {},
};

// --- Slice Creator ---
export const createOnboardingSlice: StateCreator<
  OnboardingSlice,
  [],
  [],
  OnboardingSlice
> = (set) => ({
  ...initialOnboardingState,

  setCurrentStep: (programId, step) =>
    set((state) => ({
      currentStepByProgram: {
        ...state.currentStepByProgram,
        [programId]: step,
      },
    })),

  skipStep: (programId, stepIndex) =>
    set((state) => {
      const currentSkipped = state.skippedStepsByProgram[programId] ?? [];
      // Avoid duplicates
      if (currentSkipped.includes(stepIndex)) {
        return state;
      }
      return {
        skippedStepsByProgram: {
          ...state.skippedStepsByProgram,
          [programId]: [...currentSkipped, stepIndex],
        },
      };
    }),

  resetOnboarding: (programId) =>
    set((state) => ({
      currentStepByProgram: {
        ...state.currentStepByProgram,
        [programId]: 0,
      },
      skippedStepsByProgram: {
        ...state.skippedStepsByProgram,
        [programId]: [],
      },
    })),

  clearAllOnboarding: () =>
    set({
      currentStepByProgram: {},
      skippedStepsByProgram: {},
    }),
});

// --- Selectors ---
export const onboarding = {
  /** Get current step for a program */
  currentStep: (programId: number) => (s: OnboardingSlice) =>
    s.currentStepByProgram[programId] ?? 0,

  /** Get skipped steps for a program */
  skippedSteps: (programId: number) => (s: OnboardingSlice) =>
    s.skippedStepsByProgram[programId] ?? [],

  /** Check if a specific step is skipped */
  isStepSkipped:
    (programId: number, stepIndex: number) => (s: OnboardingSlice) =>
      (s.skippedStepsByProgram[programId] ?? []).includes(stepIndex),

  /** Get all onboarding state and actions */
  state: (s: OnboardingSlice) => ({
    currentStepByProgram: s.currentStepByProgram,
    skippedStepsByProgram: s.skippedStepsByProgram,
    setCurrentStep: s.setCurrentStep,
    skipStep: s.skipStep,
    resetOnboarding: s.resetOnboarding,
    clearAllOnboarding: s.clearAllOnboarding,
  }),
};

